import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Plus, Ticket, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { formatMoney } from '../../utils/formatMoney';
import { ConnectWalletButton } from '../auth/ConnectWalletButton';

const walletPositionClass =
  'fixed right-3 top-[4.5rem] z-40 md:right-4 md:top-20';
const walletWidthClass = 'w-[min(18rem,calc(100vw-1.5rem))] md:w-72 lg:w-80';

function WalletFab({ balance, onOpen, label }) {
  return (
    <div className={walletPositionClass}>
      <button
        type="button"
        onClick={onOpen}
        title={label}
        aria-label={label}
        className="relative flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand-gold bg-white text-brand-red shadow-lg transition-transform hover:scale-105 hover:shadow-xl"
      >
        <Wallet className="h-5 w-5" />
        {balance !== undefined ? (
          <span className="absolute -bottom-1 -right-1 rounded-full bg-brand-red px-1.5 py-0.5 text-[10px] font-bold text-white">
            {balance >= 1000
              ? `$${Math.floor(balance / 1000)}k`
              : formatMoney(balance)}
          </span>
        ) : null}
      </button>
    </div>
  );
}

export function WalletBalanceCard() {
  const { isAuthenticated } = useAuth();
  const { balance, ticketHistory } = useWallet();
  const [isCollapsed, setIsCollapsed] = useState(true);

  if (!isAuthenticated) {
    if (isCollapsed) {
      return (
        <WalletFab
          label="เปิดกระเป๋าเงิน Mungkorn"
          onOpen={() => setIsCollapsed(false)}
        />
      );
    }

    return (
      <div className={`${walletPositionClass} ${walletWidthClass}`}>
        <Card className="shadow-xl">
          <CardHeader className="relative pr-10">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-5 w-5" />
              กระเป๋าเงิน Mungkorn
            </CardTitle>
            <button
              type="button"
              onClick={() => setIsCollapsed(true)}
              title="ย่อกระเป๋าเงิน"
              aria-label="ย่อกระเป๋าเงิน"
              className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-brand-red"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              สมัครสมาชิกหรือเข้าสู่ระบบด้วย Web3 wallet
            </p>
            <ConnectWalletButton variant="panel" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isCollapsed) {
    return (
      <WalletFab
        balance={balance}
        label="เปิดกระเป๋าเงิน Mungkorn"
        onOpen={() => setIsCollapsed(false)}
      />
    );
  }

  return (
    <div className={`${walletPositionClass} ${walletWidthClass}`}>
      <Card className="shadow-xl">
        <CardHeader className="relative pr-10">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-5 w-5" />
            กระเป๋าเงิน Mungkorn
          </CardTitle>
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            title="ย่อกระเป๋าเงิน"
            aria-label="ย่อกระเป๋าเงิน"
            className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-brand-red"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-xl border-2 border-brand-gold bg-gradient-to-br from-brand-gold-light/40 to-white p-4 text-center">
            <p className="text-sm text-gray-600">ยอดคงเหลือ</p>
            <p className="mt-1 text-2xl font-bold text-brand-gold">
              {formatMoney(balance)}
            </p>
          </div>

          <Link to="/wallet" className="block">
            <Button variant="gold" className="w-full">
              <Plus className="h-4 w-4" />
              ฝาก-ถอนเงิน
            </Button>
          </Link>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <Ticket className="h-4 w-4 text-brand-red" />
              <h4 className="text-sm font-semibold text-gray-800">ตั๋วล่าสุด</h4>
            </div>
            {ticketHistory.length === 0 ? (
              <p className="text-sm text-gray-500">ยังไม่มีตั๋ว</p>
            ) : (
              <ul className="max-h-32 space-y-1.5 overflow-y-auto">
                {ticketHistory.slice(0, 3).map((ticket) => (
                  <li
                    key={ticket.id}
                    className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-1.5 text-xs"
                  >
                    <span className="font-mono font-semibold text-brand-red">
                      {ticket.ticket_number}
                    </span>
                    <span className="text-gray-500">
                      {new Date(ticket.created_at).toLocaleDateString('th-TH')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
